import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2, CheckCircle2, AlertTriangle, ArrowRight, ArrowLeft, UserX } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

function parseCSV(text) {
  const rows = [];
  let i = 0, field = "", row = [], inQ = false;
  while (i < text.length) {
    const c = text[i];
    if (inQ) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i += 2; continue; }
      if (c === '"') { inQ = false; i++; continue; }
      field += c; i++; continue;
    }
    if (c === '"') { inQ = true; i++; continue; }
    if (c === ",") { row.push(field); field = ""; i++; continue; }
    if (c === "\r") { i++; continue; }
    if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; i++; continue; }
    field += c; i++;
  }
  if (field !== "" || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((v) => v && v.trim() !== ""));
}

export default function MigrationWizard() {
  const [step, setStep] = useState(1);
  const [analysis, setAnalysis] = useState(null);
  const [mapping, setMapping] = useState({});
  const [sourceId, setSourceId] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const arr = parseCSV(text);
      if (arr.length < 2) { toast.error("CSV vuoto o senza righe dati"); setBusy(false); return; }
      const columns = arr[0].map((c) => c.trim());
      const rows = arr.slice(1).map((r) => Object.fromEntries(columns.map((c, idx) => [c, r[idx] ?? ""])));
      const { data } = await api.post("/migration/analyze", { filename: file.name, columns, rows });
      setAnalysis(data);
      setMapping(data.suggested_mapping);
      setStep(2);
    } catch (err) {
      toast.error("Errore lettura file: " + (err.message || ""));
    }
    setBusy(false);
  };

  const selectSource = (id) => {
    setSourceId(id);
    const src = (analysis?.sources || []).find((s) => s.id === id);
    if (src && src.mapping && Object.keys(src.mapping).length > 0) {
      // Precarica il mapping salvato della sorgente per le colonne presenti nel file
      setMapping((m) => {
        const next = { ...m };
        for (const col of analysis.columns) {
          if (src.mapping[col]) next[col] = src.mapping[col];
        }
        return next;
      });
      toast.success("Mapping della sorgente precaricato");
    }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/migration/preview", { job_id: analysis.job_id, mapping, source_id: sourceId || null });
      setPreview(data);
      setStep(3);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore anteprima"); }
    setBusy(false);
  };

  const runImport = async () => {
    setBusy(true);
    try {
      if (sourceId) {
        try { await api.put(`/sources/${sourceId}`, { mapping }); } catch { /* non bloccante */ }
      }
      const { data } = await api.post("/migration/import", { job_id: analysis.job_id, mapping, source_id: sourceId || null });
      setResult(data);
      setStep(4);
      toast.success("Migrazione completata");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore importazione"); }
    setBusy(false);
  };

  const reset = () => { setStep(1); setAnalysis(null); setMapping({}); setSourceId(""); setPreview(null); setResult(null); };

  const Steps = () => (
    <div className="flex items-center gap-2 text-xs mb-4">
      {["Carica CSV", "Mappa colonne", "Anteprima", "Importa"].map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <span className={cn("w-6 h-6 rounded-full grid place-items-center font-bold", step >= i + 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>{i + 1}</span>
          <span className={cn(step >= i + 1 ? "font-medium" : "text-muted-foreground")}>{s}</span>
          {i < 3 && <span className="text-muted-foreground">/</span>}
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm">
        Migrazione dei <b>lead storici</b> dal Google Sheet. Esporta il foglio in <b>CSV</b> e caricalo qui: analizzeremo le colonne, verificherai il mapping e vedrai un'anteprima con i conteggi <b>prima</b> di importare. L'import è <b>ri-eseguibile in sicurezza</b> senza creare duplicati.
      </div>
      <Steps />

      {step === 1 && (
        <label className="block rounded-lg border-2 border-dashed p-10 text-center cursor-pointer hover:bg-accent/50 transition-colors" data-testid="migration-upload">
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
          {busy ? <Loader2 className="w-8 h-8 mx-auto animate-spin text-muted-foreground" /> : <Upload className="w-8 h-8 mx-auto text-muted-foreground" />}
          <div className="mt-3 font-medium">Clicca per caricare il CSV</div>
          <div className="text-xs text-muted-foreground mt-1">Esporta il tuo Google Sheet come .csv</div>
        </label>
      )}

      {step === 2 && analysis && (
        <div className="space-y-3">
          <div className="rounded-lg border bg-card p-3 space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Sorgente di destinazione (opzionale ma consigliata)</label>
            <Select value={sourceId || "__none"} onValueChange={(v) => selectSource(v === "__none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="migration-source-select"><SelectValue placeholder="Nessuna sorgente" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Nessuna sorgente (import generico)</SelectItem>
                {(analysis.sources || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.campaign ? ` · ${s.campaign}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">Associando una sorgente, i lead erediteranno fonte/campagna/argomento di default della sorgente (se non presenti nella riga) e la deduplica sarà circoscritta a quella sorgente. Il mapping verrà salvato sulla sorgente.</p>
          </div>
          <p className="text-sm text-muted-foreground">{analysis.total_rows} righe trovate. Associa ogni colonna del file a un campo CRM.</p>
          <div className="rounded-lg border bg-card divide-y max-h-[420px] overflow-y-auto">
            {analysis.columns.map((col) => (
              <div key={col} className="flex items-center gap-3 px-4 py-2.5" data-testid={`map-row-${col}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{col}</div>
                  <div className="text-xs text-muted-foreground truncate">es: {(analysis.sample[0]?.[col] ?? "—") || "—"}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                <Select value={mapping[col] || "skip"} onValueChange={(v) => setMapping((m) => ({ ...m, [col]: v }))}>
                  <SelectTrigger className="h-8 w-56" data-testid={`map-select-${col}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{analysis.crm_fields.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            <Button variant="ghost" onClick={reset}><ArrowLeft className="w-4 h-4 mr-1" />Ricomincia</Button>
            <Button onClick={runPreview} disabled={busy} data-testid="migration-preview-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Genera anteprima<ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {step === 3 && preview && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[
              ["Totale righe", preview.counts.total, ""],
              ["Nuovi lead", preview.counts.new, "text-sky-600"],
              ["Rinvii richiesta (stessa campagna)", preview.counts.resubmission, "text-amber-600"],
              ["Righe duplicate (saltate)", preview.counts.skipped, "text-muted-foreground"],
              ["Commerciale riconosciuto", preview.counts.owner_recognized, "text-emerald-600"],
              ["Commerciale NON riconosciuto", preview.counts.owner_unrecognized, "text-red-600"],
              ["Note da importare", preview.counts.notes, ""],
              ["Follow-up da creare", preview.counts.followups, ""],
              ["Righe con errori", preview.counts.errors, "text-red-600"],
            ].map(([l, v, c]) => (
              <div key={l} className="rounded-lg border bg-card p-3" data-testid={`preview-${l}`}>
                <div className={cn("font-heading text-2xl font-black tabular-nums", c)}>{v}</div>
                <div className="text-xs text-muted-foreground">{l}</div>
              </div>
            ))}
          </div>
          <div className="rounded-md border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-xs text-muted-foreground">
            Regola: ogni richiesta è un lead distinto. Stesso contatto con <b>campagna/argomento diversi</b> → nuovo lead. Stesso contatto con <b>stessa campagna</b> → nessun nuovo lead, viene aggiunta una nota «ha rimandato la richiesta». La stessa identica riga non viene mai importata due volte.
          </div>
          {preview.unrecognized_owners.length > 0 && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm">
              <div className="flex items-center gap-2 font-medium mb-1"><UserX className="w-4 h-4 text-amber-500" />Commerciali non riconosciuti</div>
              <div className="text-xs text-muted-foreground">Questi lead verranno importati come "Da assegnare" conservando il nome originale: {preview.unrecognized_owners.join(", ")}</div>
            </div>
          )}
          {preview.errors_sample.length > 0 && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm">
              <div className="flex items-center gap-2 font-medium mb-1"><AlertTriangle className="w-4 h-4 text-red-500" />Errori</div>
              <ul className="text-xs text-muted-foreground list-disc pl-4">{preview.errors_sample.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" />Modifica mapping</Button>
            <Button onClick={runImport} disabled={busy} data-testid="migration-import-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Esegui importazione</Button>
          </div>
        </div>
      )}

      {step === 4 && result && (
        <div className="space-y-4">
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
            <div className="font-heading font-bold text-lg">Migrazione completata</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[["Lead creati", result.created], ["Rinvii richiesta", result.resubmission], ["Saltati (duplicati)", result.skipped], ["Errori", result.errors]].map(([l, v]) => (
              <div key={l} className="rounded-lg border bg-card p-3" data-testid={`result-${l}`}>
                <div className="font-heading text-2xl font-black tabular-nums">{v}</div>
                <div className="text-xs text-muted-foreground">{l}</div>
              </div>
            ))}
          </div>
          <Button onClick={reset} variant="outline">Nuova migrazione</Button>
        </div>
      )}
    </div>
  );
}
